


/// <amd-module name="DistributedTaskUI/Logs/ExpandedLogsViewerComponent" />
import * as React from "react";

import * as RegexConstants from "DistributedTaskUI/Logs/LiveLogsParser";
import * as Resources from "DistributedTaskUI/Logs/Resources";

import { Button } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { CustomHeader } from "VSSUI/Header";
import { CustomPanel, PanelContent } from "VSSUI/Panel";
import { Toast } from "VSSUI/Toast";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { IRenderFunction } from "OfficeFabric/Utilities";
import { css, KeyCode } from "VSSUI/Util";

import { IVssIconProps, VssIcon } from "VSSUI/VssIcon";

import { isFeatureEnabled } from "VSS/Platform/Feature";
import { ReactRootComponent, IVssLayoutManager, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";
import { IStatusProps, Status, StatusSize } from "VSSUI/Status";


import { format } from "VSS/Core/Util/String";
import "VSS/LoaderPlugins/Css!DistributedTaskUI/Logs/ExpandedLogsViewerComponent";
import { AnsiParser, ansiEscapeCodeRegexString, RegexToIdentify } from "./AnsiParser";
import { ILineSelection, ILinkingOptions } from "./Logs.Types";

import { IEditorProps} from "VSS/Features/Monaco/Contracts";

import { Reader } from "DistributedTaskUI/LogReader/Reader";

export interface ILogsViewerSection {
    /**
     * Sets focus to the Monaco editor.
     */
    focus: () => void;
}

export interface IExpandedLogsViewerComponentProps {
    content: string | undefined;
    showPanel: boolean;
    followTail: boolean;
    onCloseClick: () => void;
    fetchLogInProgress?: boolean;
    showLineNumber?: boolean;
    isLive?: boolean;
    headerTitle?: string;
    headerstatusProps?: IStatusProps;
    onRenderHeader?: IRenderFunction<IExpandedLogsViewerComponentProps>;
    pageContext?: IVssPageContext;
    componentRef?: (component: ILogsViewerSection | null) => void;
    linkingOptions?: ILinkingOptions;
    selection?: ILineSelection;
    hideTimestamp?: boolean;

    /**
     * Only used by new log reader. Call back that gets triggered when the line has been scroll to view successfully, when 'lineToScroll' prop is set
     */
    onScrollToLine?: (lineNumber: number) => void;

    /**
     * @deprecated use statusProps
     */
    headerIconProps?: IVssIconProps;
    /**
     * @deprecated use selection
     */
    defaultLineNumber?: number;
}

export interface IExpandedLogsViewerComponentState {
    content: string | undefined;
    showLineNumber: boolean;
}

const codeEditorComponentType = "vss-code-editor-type";
// Store the original content in the editor model, making it easy to compare
const originalContentProp = "vss-content";


let monacoLanguagesRegistered = false;
function registerMonacoLanguages() {
    if (!monacoLanguagesRegistered) {
        monacoLanguagesRegistered = true;

        monaco.languages.register({
            id: "vsts-dtc-log-static",
            mimetypes: ["text/vsts-dtc-log-static"]
        });

        monaco.languages.setMonarchTokensProvider("vsts-dtc-log-static", {
            tokenizer: { root: RegexConstants.StaticLogTokenizer },
            ignoreCase: true,
            tokenPostfix: ""
        });

        monaco.languages.register({
            id: "vsts-dtc-log-live",
            mimetypes: ["text/vsts-dtc-log-live"]
        });

        monaco.languages.setMonarchTokensProvider("vsts-dtc-log-live", {
            tokenizer: { root: RegexConstants.LiveLogsTokenizer },
            ignoreCase: true,
            tokenPostfix: ""
        });
    }
}

export class ExpandedLogsViewerComponent extends React.Component<IExpandedLogsViewerComponentProps, IExpandedLogsViewerComponentState> implements ILogsViewerSection {
    public componentWillMount(): void {
        /**
         * Hack to let monaco editor render HTML
         */
        (window as any).HTMLRegex = RegexToIdentify;
        this.setState({
            content: this.props.content,
            showLineNumber: !!this.props.showLineNumber
        });
    }

    public componentWillReceiveProps(nextProps: IExpandedLogsViewerComponentProps, nextState: IExpandedLogsViewerComponentState) {
        if (nextProps.content) {
            const parsed = nextProps.hideTimestamp ? nextProps.content.replace(/.{27}Z /gm, "") : nextProps.content;
            const showLineNumber = !!nextProps.showLineNumber;
            if (this.state.content !== parsed || this.state.showLineNumber !== showLineNumber) {
                this.setState({
                    content: parsed,
                    showLineNumber: showLineNumber
                });
                if (this.state.content !== parsed) {
                    // since we send lazyVisualContentChange, we need to take over updating the editor content
                    if (this._editor) {
                        const model = this._editor.getModel();
                        if (model) {
                            const oldContent = model[originalContentProp] || model.getValue();
                            if (oldContent !== parsed) {
                                model.setValue(parsed);
                                this._handleContent();
                            }
                        }
                    }
                }
            }
        }
    }

    public componentDidMount() {
        if (this.props.componentRef) {
            this.props.componentRef(this);
        }

        window.addEventListener("resize", this._onResizeHandler);
    }

    public render(): React.ReactNode {
        return (
            this.props.showPanel && (
                <CustomPanel
                    onDismiss={this._onCloseButtonClick}
                    size={ContentSize.Large}
                    contentClassName="logs-expanded-panel"
                    className={"logs-expanded-view-panel"}>
                    <div className="logs-expand-view custom-scrollbar flex-column flex-grow" ref={(element) => this._logsViewerElement = element}>
                        <CustomHeader>
                            {this._getHeaderContent()}
                        </CustomHeader>
                        <PanelContent>
                            {this._getLogsSection()}
                        </PanelContent>
                    </div>
                </CustomPanel>
            )
        );
    }

    public componentWillUnmount(): void {
        window.removeEventListener("resize", this._onResizeHandler);

        if (this._editor) {
            if (this._editor.model) {
                delete this._editor.model[originalContentProp];
            }
            this._editor.dispose();
            this._editor = null;
        }

        (window as any).HTMLRegex = undefined;
    }

    public focus() {
        if (this._editor) {
            this._editor.focus();
        }
    }

    private _onResizeHandler = () => {
        if (this._editor) {
            this._editor.layout();
        }
    }

    private _getHeaderContent(): React.ReactNode {
        if (this.props.onRenderHeader) {
            return this.props.onRenderHeader(this.props, this._getDefaultHeaderTitleSection);
        } else {
            return this._getDefaultHeaderTitleSection(this.props);
        }
    }

    private _getDefaultHeaderTitleSection = (props: IExpandedLogsViewerComponentProps): JSX.Element => {
        const iconProps = this.props.headerIconProps;
        const statusProps = this.props.headerstatusProps;
        const headerClassName = "logs-expanded-view-task-status-icon";
        return (
            <div className="logs-expanded-view-header flex" role="region" aria-label={Resources.LogsComponentHeaderRegionAriaLabel}>
                <div className="logs-view-header-left-section flex">
                    <div className="header-title-container flex">
                        {
                            statusProps && (
                                <Status {...statusProps} size={StatusSize.m} className={headerClassName} />
                            )
                        }
                        {
                            !statusProps && iconProps &&
                            <VssIcon
                                className={css(headerClassName, "log-task-state", iconProps.className)}
                                iconName={iconProps.iconName}
                                iconType={iconProps.iconType}
                            />
                        }

                        <div className={"header-title-container-tooltip-host"}>
                            <Tooltip overflowOnly={true} showOnFocus={true} text={this.props.headerTitle}>
                                <div role="heading" aria-level={1} tabIndex={0} onKeyDown={this._onkeyDown} className="header-title ellipsis-overflow">{this.props.headerTitle}</div>
                            </Tooltip>
                        </div>
                    </div>
                </div>
                <div className="logs-view-header-right-section">
                    <Button
                        subtle={true}
                        ariaLabel={Resources.TaskLogExpandedViewCloseButtonAriaLabel}
                        className={"logs-expanded-view-close-button"}
                        iconProps={{ className: "bowtie-icon bowtie-navigate-close logs-expanded-view-close-icon" }}
                        onClick={this._onCloseButtonClick}
                        disabled={false} />
                </div>
            </div>
        );
    }

    private _onkeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
        if (this._editor && evt && evt.shiftKey && evt.keyCode === KeyCode.tab) {
            this._editor.focus();
        }
    }

    private _onCloseButtonClick = (): void => {
        if (this.props.onCloseClick) {
            this.props.onCloseClick();
        }
    }

    private _getLogsSection(): React.ReactNode {
        let component: JSX.Element;
        const legacyComponentLoading: () => JSX.Element = () => (<Spinner size={SpinnerSize.large} label={Resources.LoadingLegacyComponent} />);
        if (this.props.pageContext && isFeatureEnabled(this.props.pageContext, "ms.vss-distributedtask-web.log-reader", false)) {
            component = <Reader
                content={this.state.content}
                lineToScroll={this.props.selection ? this.props.selection.lineStart : undefined}
                {...this.props.linkingOptions ? {
                    onLineClick: this._readerOnLink
                } : {}}
                autoScroll={this.props.followTail}
                onScrollToLine={this.props.onScrollToLine}>
            </Reader>;
        }
        else {
            const editorProps: IEditorProps = {
                className: "logs-editor-host",
                editorOptions: this._getEditorOptions(),
                monacoTheme: "vs-dark",
                content: {
                    content: this.state.content!,
                    contentType: this.props.isLive ? "vsts-dtc-log-live" : "vsts-dtc-log-static"
                },
                editorRef: this._onEditorCreated,
                onContentUpdated: this._onEditorContentUpdated,
                lazyVisualContentChange: true,
                ...(this.props.linkingOptions ? {
                    getSelectedTextLink: this._getLinkFromRange
                } : {})
            };

            component = (<WrappedComponent
                dependencies={["ms.vss-features.editor"]}
                wrappedType="code-editor"
                wrappedProps={editorProps}
                loadingComponent={legacyComponentLoading}
            />);
        }


        return (
            <div className="logs-expanded-view-logs-section" role="region" aria-label={Resources.LogsComponentContentRegionAriaLabel}>
                {
                    this.props.fetchLogInProgress ?
                        <Spinner
                            className={"logs-view-spinner"}
                            label={Resources.FetchingLogLoadingComponentLabel}
                            size={SpinnerSize.large}
                        />
                        : (this.props.pageContext) ? (
                            <ReactRootComponent
                                pageContext={this.props.pageContext}
                            >
                                {component}
                            </ReactRootComponent>)
                            : component
                }
            </div>
        );
    }

    private _getEditorOptions(): any /*IEditorOptions*/ {
        return {
            readOnly: true,
            lineNumbers: this.state.showLineNumber ? "on" : "off",
            scrollbar: { horizontalScrollbarSize: 16 },
            extraEditorClassName: "logs-view-monaco-editor",
        } /*as IEditorOptions*/;
    }

    private _getLinkFromRange = (range: monaco.IRange): string => {
        return this._getLink({
            lineStart: range.startLineNumber,
            lineEnd: range.endLineNumber,
            colStart: range.startColumn,
            colEnd: range.endColumn
        });
    }

    private _getLink = (selection: ILineSelection): string => {
        const options = this.props.linkingOptions;
        if (options) {
            return options.getLink(options.data, selection);
        }

        return "";
    }

    /**
     * This is used by new log reader
     */
    private _readerOnLink = (lineNumber: number): void => {
        if (this.props.pageContext) {
            const link = this._getLink({
                lineStart: lineNumber
            } as ILineSelection);

            // copy link
            // IE11 Compatible
            let copySuccess = false;
            if ((window as any).clipboardData) {
                (window as any).clipboardData.setData("text", link);
                copySuccess = true;
            }

            const listener = (e: ClipboardEvent) => {
                if (e.clipboardData) {
                    e.clipboardData.setData("text", link);
                    copySuccess = true;
                    e.preventDefault();
                }
            };
            document.addEventListener("copy", listener);
            document.execCommand("copy");
            document.removeEventListener("copy", listener);

            const layoutManager = this.props.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout((onDismiss) => {
                const element = React.createElement(FadeToast, {
                    content: copySuccess ? format(Resources.CopiedLink, lineNumber) : format(Resources.CopyLinkFailed, link)
                }, null);
                return element;
            });
        }

    }

    private _onEditorContentUpdated = (editor: any /*IEditor*/) => {
        if (editor && this.props.followTail) {
            const model = editor.getModel() /*as IEditorModel*/;
            const lineCount = model.getLineCount();
            editor.revealLine(lineCount);
        }
    }

    private _onEditorCreated = (editor: any /*IEditor*/) => {
        registerMonacoLanguages();
        if (editor) {
            this._editor = editor;
            this._handleContent();
            const selection = this.props.selection;
            if (selection) {
                if (selection.lineStart && !selection.lineEnd) {
                    // if there's only lineStart, with out lineends, reveal it, it's not a selection
                    editor.revealLine(selection.lineStart);
                }
                else {
                    editor.setSelection({
                        startLineNumber: selection.lineStart,
                        endLineNumber: selection.lineEnd,
                        startColumn: selection.colStart,
                        endColumn: selection.colEnd
                    });
                }
            }
            else if (this.props.defaultLineNumber && this.props.defaultLineNumber > 0) {
                editor.revealLine(this.props.defaultLineNumber);
            }
            else {
                editor.focus();
            }

            // Hardcoded to 2 (keycode for TAB) as there is no easy way to have monaco editor type reference
            // Monaco editor keycode https://microsoft.github.io/monaco-editor/api/enums/monaco.keycode.html#tab
            editor.addCommand(2, () => {
                if (this._logsViewerElement) {
                    const tabbableElement = this._logsViewerElement.querySelectorAll("[tabindex='0']");
                    if (tabbableElement) {
                        const firstFocusableElement = tabbableElement[0] as HTMLElement;
                        if (firstFocusableElement) {
                            firstFocusableElement.focus();
                        }
                    }
                }
            });

            // Bind to escape to hide panel
            // Hardcoded to 9 (keycode for Escape) as there is no easy way to have monaco editor type reference
            // Monaco editor keycode https://microsoft.github.io/monaco-editor/api/enums/monaco.keycode.html#tab
            editor.onKeyDown((e: any) => {
                if (e) {
                    if (e.keyCode === 9) {
                        this._onCloseButtonClick();
                    }
                }
            });
        }
    }

    private _handleContent = () => {
        const editor = this._editor;
        if (!editor) {
            return;
        }

        // This section pokes around editor to get ansii escape codes to work, this is non-critical, so try catch this whole thing just to make sure we don't affect the whole experience
        try {
            // Since we inject our tags, when user copies, we don't want to pollute the copy, so strip them off here
            // This is a temporary implementation, ideally we should be able to create our new model and do the same instead of saving off the original
            //  but since we use the editor from legacy code, this is probably the best thing to do
            const original = editor.viewModel.getPlainTextToCopy;
            editor.viewModel.getPlainTextToCopy = (ranges: Range[], emptySelectionClipboard: boolean, forceCRLF: boolean): string | string[] => {
                let originalData: string | string[] = original.apply(editor.viewModel, [ranges, emptySelectionClipboard, forceCRLF]);
                const stripTags = function (content: string) {
                    content = content || "";
                    content = content.replace(/ (class='[a-z- A-Z]+')/g, "");
                    content = content.replace(/<span>/g, "");
                    content = content.replace(/<\/span>/g, "");
                    return content;
                };

                let newData: string | string[];
                if (Array.isArray(originalData)) {
                    newData = [];
                    originalData.forEach(x => {
                        (newData as string[]).push(stripTags(x));
                    });
                }
                else {
                    newData = stripTags(originalData);
                }

                return newData;
            };

            // Parse ANSII codes in seperate stack
            // Note: Monaco will look for the regex we set window.HTMLRegex to recognize if it needs to render HTML
            //        the side effect of this is that if you have text that contains BOTH html content AND Ansi escape code, it will render bit garbled - eg: <div>REDDDD</div> �[34m�[1mclang version�[0m
            //        however normal html will be escaped properly as it won't have the regEx we append
            setTimeout(() => {
                // searchString: string, searchOnlyEditableRange: boolean, isRegex: boolean, matchCase: boolean, wordSeparators: string | null, captureMatches: boolean, limitResultCount?: number returns FindMatch[]
                if (!editor.model) {
                    return;
                }

                let matches = editor.model.findMatches(ansiEscapeCodeRegexString, false, true, undefined, undefined, undefined, 9999999);
                matches = matches || [];
                let lineNumbers = new Set();
                const parser = new AnsiParser();
                matches.forEach((x: any) => {
                    lineNumbers.add(x.range.startLineNumber);
                });
                let edits: any = [];
                lineNumbers.forEach(x => {
                    const content = editor.model.getLineContent(x);
                    const newContent = parser.parse(content);
                    edits.push({
                        text: newContent,
                        range: {
                            startLineNumber: x,
                            endLineNumber: x,
                            startColumn: 1,
                            endColumn: content.length + 1
                        }
                    });
                });
                // store original content
                editor.model[originalContentProp] = this.state.content;
                editor.model.applyEdits(edits);
            }, 0);

            // Replace any NULL characters in seperate stack
            setTimeout(() => {
                if (!editor.model) {
                    return;
                }
                
                let matches = editor.model.findMatches("\u0000", false, false, undefined, undefined, undefined, 9999999);
                matches = matches || [];
                editor.model.applyEdits(matches.map((x: any) => {
                    return {
                        text: " ",
                        range: x.range
                    };
                }));
            }, 0);
        }
        catch (ex) {
            console.error(ex);
        }
    }

    private _editor: any /*IEditor*/;
    private _logsViewerElement: HTMLElement | null;
}

interface IFadeToastProps {
    content: string;
}

class FadeToast extends React.Component<IFadeToastProps> {
    private _toastRef: React.RefObject<Toast> = React.createRef<Toast>();
    public render() {
        return <Toast ref={this._toastRef} message={this.props.content}>
        </Toast>;
    }

    public componentDidMount() {
        setTimeout(() => {
            if (this._toastRef && this._toastRef.current) {
                this._toastRef.current.fadeOut();
            }
        }, 2000);
    }
}
